import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { NavigateFunction } from "react-router-dom";
import { getCardType, validateCPF, validateCNPJ } from "@/utils/functions";
import useCybersourceService from "@/hooks/useCybersourceService";
import moment from "moment";
import { paymentCard } from "@/redux/ducks/user";
import { ReducersTypes } from "@/redux/reducers/Models";
import { PaymentCardAction } from "@/redux/sagas/payment/card";

const creditCardSchema = z.object({
  number: z.string().refine(val => getCardType(val) && val?.length >= 18, "Número inválido"),
  name: z.string().refine(val => val.length > 1 && /\D/.test(val ?? ""), "Nome inválido"),
  expiration: z.string().refine(
    (val) => {
      if (!/^\d{2}\/\d{4}$/.test(val)) {
        return false;
      }
      const [month] = val.split('/');
      const monthNum = parseInt(month, 10);
      
      if (monthNum < 1 || monthNum > 12) {
        return false;
      }
      
      const expiration = moment(val, "MM/YYYY", true);
      return expiration.isValid() && moment().diff(expiration, 'M') < 0;
    },
    (val) => {
      if (!/^\d{2}\/\d{4}$/.test(val)) {
        return { message: "Por favor, insira o ano de vencimento com 4 dígitos" };
      }
      
      const [month, year] = val.split('/');
      const monthNum = parseInt(month, 10);
      const yearNum = parseInt(year, 10);
      const currentYear = moment().year();
      
      if (monthNum < 1 || monthNum > 12) {
        if (yearNum < currentYear) {
          return { message: "Mês e ano de vencimento inválidos. Insira um mês de vencimento válido e um ano de vencimento futuro." };
        }
        return { message: "Mês de vencimento inválido." };
      }
      
      const expiration = moment(val, "MM/YYYY", true);
      if (!expiration.isValid() || moment().diff(expiration, 'M') >= 0) {
        return { message: "Ano de vencimento inválido: o cartão informado está expirado" };
      }
      
      return { message: "Ano de vencimento inválido: o cartão informado está expirado" };
    }
  ),
  isForeign: z.boolean(),
  document: z.string(),
}).superRefine((data, ctx) => {
  if (data.isForeign) {
    const cleanValue = data.document.replace(/[^a-zA-Z0-9]/g, '');
    if (cleanValue.length === 0) {
      return;
    }
    if (cleanValue.length < 6) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Documento deve ter no mínimo 6 caracteres",
        path: ["document"],
      });
    } else if (cleanValue.length > 11) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Documento deve ter no máximo 11 caracteres",
        path: ["document"],
      });
    }
  } else {
    const cleanValue = data.document.replace(/\D/g, '');
    
    if (cleanValue.length === 0) {
      return;
    }
    
    if (cleanValue.length === 11) {
      const cpfWithMask = cleanValue.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
      if (!validateCPF(cpfWithMask)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "CPF inválido",
          path: ["document"],
        });
      }
    } else if (cleanValue.length === 14) {
      const cnpjWithMask = cleanValue.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
      if (!validateCNPJ(cnpjWithMask)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "CNPJ inválido",
          path: ["document"],
        });
      }
    } else {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Digite um CPF ou CNPJ válido",
        path: ["document"],
      });
    }
  }
});

type FormData = {
  number: string;
  name: string;
  expiration: string;
  isForeign: boolean;
  document: string;
};

export default function useCheckoutCardForm({ navigate }: { navigate: NavigateFunction }) {
  const dispatch = useDispatch();
  const { getSessionId } = useCybersourceService();
  const user = useSelector((state: ReducersTypes) => state.userReducer);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    trigger,
    clearErrors,
    formState: { errors, isValid },
  } = useForm<FormData>({
    resolver: zodResolver(creditCardSchema),
    mode: "onChange",
    defaultValues: {
      isForeign: false,
    },
  });

  const handlePayment = useCallback((success: boolean) => {
    setIsLoading(false);

    if (!success) {
      setError(true);
      return;
    }

    navigate('/checkout/finished');
  }, [navigate]);

  const onSubmit = useCallback(
    (data: FormData) => {
      if (isLoading) return;

      setIsLoading(true);
      setError(false);

      const card = {
        number: data.number,
        name: data.name,
        expiration: data.expiration,
        document: data.document,
      };

      const visitorId = getSessionId()!;

      const payload: PaymentCardAction['payload'] & {
        name?: string;
        email?: string;
        phone?: string;
      } = {
        plate: user?.plate ?? "",
        card,
        address: {
          zipcode: user?.address?.zipcode ?? "",
          streetName: user?.address?.streetName ?? "",
          neighborhood: user?.address?.neighborhood ?? "",
          addressStreet: user?.address?.streetName ?? "",
          number: user?.address?.number ?? "",
          complement: user?.address?.complement ?? "",
          city: user?.address?.city ?? "",
          state: user?.address?.state ?? "",
          stateCode: user?.address?.stateCode ?? "",
        },
        visitorId,
        // @ts-expect-error
        debts: user?.debts ?? [],
        cart: user?.cart ?? [],
        requestId: user?.requestId ?? "",
        callback: handlePayment,
        // Dados do usuário para fluxo avulso
        name: user?.name as string | undefined,
        email: user?.email as string | undefined,
        phone: user?.phone as string | undefined,
      };

      dispatch(paymentCard(payload));
    },
    [isLoading, dispatch, handlePayment, getSessionId, user]
  );

  return {
    isLoading,
    isDisabled: !isValid || isLoading,
    error,
    register,
    handleSubmit: handleSubmit(onSubmit),
    errors,
    setValue,
    watch,
    trigger,
    clearErrors,
  };
} 